import type { User } from '@/types'

export type AccessRole = 'superadmin' | 'admin' | 'user'

export type PagePermission =
  | 'dashboard'
  | 'chat'
  | 'contatos'
  | 'monitoramento'
  | 'configuracoes.geral'
  | 'configuracoes.instancias'
  | 'configuracoes.usuarios'

export type ElementPermission =
  | '*'
  | 'dashboard'
  | 'chat'
  | 'contatos'
  | 'monitoramento.todos_elementos'
  | 'configuracoes.geral.perfil'
  | 'configuracoes.geral.tags_customizadas'
  | 'configuracoes.geral.notificacoes'
  | 'configuracoes.geral.api_token_visualizar'
  | 'configuracoes.geral.company'
  | 'configuracoes.geral.webhook_central'
  | 'configuracoes.geral.branding'
  | 'configuracoes.geral.openai'
  | 'configuracoes.geral.google'
  | 'configuracoes.instancias'
  | 'configuracoes.usuarios'

interface RolePermissionMatrix {
  pages: PagePermission[]
  elements: ElementPermission[]
}

const permissions: Record<AccessRole, RolePermissionMatrix> = {
  superadmin: {
    pages: [
      'dashboard',
      'chat',
      'contatos',
      'monitoramento',
      'configuracoes.geral',
      'configuracoes.instancias',
      'configuracoes.usuarios',
    ],
    elements: ['*'],
  },
  admin: {
    pages: [
      'dashboard',
      'chat',
      'contatos',
      'monitoramento',
      'configuracoes.geral',
      'configuracoes.instancias',
      'configuracoes.usuarios',
    ],
    elements: [
      'dashboard',
      'chat',
      'contatos',
      'monitoramento.todos_elementos',
      'configuracoes.geral.perfil',
      'configuracoes.geral.tags_customizadas',
      'configuracoes.geral.notificacoes',
      'configuracoes.geral.api_token_visualizar',
      'configuracoes.instancias',
      'configuracoes.usuarios',
    ],
  },
  user: {
    pages: ['chat', 'contatos'],
    elements: ['chat', 'contatos'],
  },
}

export function resolveAccessRole(user: Pick<User, 'role' | 'is_superadmin'> | null | undefined): AccessRole | null {
  if (!user) return null
  if (user.is_superadmin) return 'superadmin'
  if (user.role === 'admin') return 'admin'
  return 'user'
}

export function canAccessPageForRole(role: AccessRole | null, page: PagePermission): boolean {
  if (!role) return false
  return permissions[role].pages.includes(page)
}

export function canAccessElementForRole(role: AccessRole | null, element: ElementPermission): boolean {
  if (!role) return false
  const rolePermissions = permissions[role]
  return rolePermissions.elements.includes('*') || rolePermissions.elements.includes(element)
}

export function canAccessPage(user: Pick<User, 'role' | 'is_superadmin'> | null | undefined, page: PagePermission): boolean {
  return canAccessPageForRole(resolveAccessRole(user), page)
}

export function canAccessElement(user: Pick<User, 'role' | 'is_superadmin'> | null | undefined, element: ElementPermission): boolean {
  return canAccessElementForRole(resolveAccessRole(user), element)
}

export function getDefaultRouteForRole(role: AccessRole | null): '/dashboard' | '/chat' {
  return role === 'superadmin' || role === 'admin' ? '/dashboard' : '/chat'
}
